import {
  Cache,
  objectSignature,
  warnLog,
  cache,
  retry,
  HttpError
} from "@zyzgroup/core-common";

type FetchResponseType = "arraybuffer" | "blob" | "text" | "json";

type FetchRequestOptions = RequestInit & {
  responseType?: FetchResponseType;
  onProgress?: (e: ProgressEvent) => void;
  timeout?: number;
  // headers?: HeadersInit;
};

const cancels = new Cache<AbortController>();

export function getFetchId(url: string, options: FetchRequestOptions) {
  return objectSignature({ url, ...options });
}

// options = { cache: "no-store" }
export async function useFetch(url: string, options: FetchRequestOptions) {
  if (typeof fetch === "undefined") {
    throw new Error("fetch() not supported!");
  }
  if (typeof AbortController === "undefined") {
    throw new Error("AbortController not supported!");
  }

  try {
    const id = getFetchId(url, options);
    const controller = new AbortController();
    cancels.set(id, controller);

    const { responseType, onProgress, timeout } = {
      responseType: "json",
      timeout: 60000,
      ...options
    };

    const timeoutId = setTimeout(() => {
      controller.abort(); // AbortError
      // throw new Error(`${timeout} ms Timeout!`);
    }, timeout);

    let response: Response = await fetch(
      new Request(url, {
        ...options,
        signal: controller.signal
        // signal: AbortSignal.timeout(timeout) // TimeoutError
        // headers: new Headers(headers),
        // credentials: "include" || "same-origin"
      })
    );

    cancels.delete(id);
    clearTimeout(timeoutId);

    // if (!response.ok) {
    //   throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    // }

    if (response.status === 200 || response.status === 0) {
      // Some browsers return HTTP Status 0 when using non-http protocol
      // e.g. 'file://' or 'data://'. Handle as success.
      if (response.status === 0) {
        warnLog("useFetch: HTTP Status 0 received.");
      }

      // Workaround: Checking if response.body === undefined for Alipay browser #23548
      // periodically read data into the new stream tracking while download progress
      if (
        onProgress &&
        typeof ReadableStream !== "undefined" &&
        response.body &&
        response.body.getReader
      ) {
        const reader = response.body.getReader();
        // Nginx needs X-File-Size check
        // https://serverfault.com/questions/482875/why-does-nginx-remove-content-length-header-for-chunked-content
        const contentLength =
          response.headers.get("X-File-Size") ||
          response.headers.get("Content-Length");
        const total = contentLength ? parseInt(contentLength) : 0;
        const lengthComputable = total !== 0;
        let loaded = 0;
        const stream = new ReadableStream({
          start(controller) {
            function read() {
              reader
                .read()
                .then(({ done, value }) => {
                  if (done) {
                    controller.close();
                  } else {
                    loaded += value.byteLength;
                    onProgress?.(
                      new ProgressEvent("progress", {
                        lengthComputable,
                        loaded,
                        total
                      })
                    );
                    controller.enqueue(value);
                    read();
                  }
                })
                .catch((e) => {
                  controller.error(e);
                });
            }
            read();
          }
        });
        response = new Response(stream);
      }

      switch (responseType) {
        case "arraybuffer":
          return await response.arrayBuffer();
        case "blob":
          return await response.blob();
        case "json":
          return await response.json();
        case "text":
        default:
          return await response.text();
      }
    } else {
      throw new HttpError(
        `useFetch for "${response.url}" responded with ${response.status}: ${response.statusText}`,
        response
      );
    }
  } catch (error: any) {
    if (error.name === "AbortError") {
      throw new Error("Request timed out");
    } else if (error.name === "TimeoutError") {
      throw new Error("Request timed out");
    }
    throw error;
  }
}

export function cancelFetch(url: string, options: FetchRequestOptions) {
  const cancel = cancels.get(getFetchId(url, options));
  if (cancel) {
    cancel.abort();
  }
}

export function useFetchRetry(maxRetries = 3, retryDelay?: number) {
  // async
  return retry(useFetch, maxRetries, retryDelay);
}

export function useFetchCache(ttlMs = 60000) {
  // async
  return cache(useFetch, ttlMs);
}

export function useFetchRetryCache(
  ttlMs = 60000,
  maxRetries = 3,
  retryDelay?: number
) {
  // async
  return cache(retry(useFetch, maxRetries, retryDelay), ttlMs);
}
